/**
 * Which permission verb actually authorizes a WRITE in each module.
 *
 * There is no `create` verb anywhere in services/rbac/modules.json — the vocabulary is
 * per-module (`govern`, `author`, `rotate`, `configure`, `manage_tokens`), because
 * rbac-authz's grammar rejects vague verbs. So `<Can action="create">` could never be
 * true for a non-superadmin, and for a superadmin it rendered a button the server would
 * still judge by a different verb. This map is the single place the UI gate is kept in
 * step with what each model's `capabilityFor` actually enforces server-side.
 *
 * Keep in sync with models/<Model>.model.js `capabilityFor.create`; the
 * writeAction.test.js drift guard fails if a module here names a verb that
 * modules.json does not declare.
 */
const WRITE_ACTION = {
  core: 'manage_tokens',   // models/ApiToken.model.js — issue/rotate/revoke
  agents: 'govern',        // models/AppAiAgent.model.js
  tools: 'author',         // models/AppDynamicTool.model.js
  // models/AppToolProvider.model.js — note `author` covers approving a synced manifest
  // too, which is a real privilege: it decides whether a model may invoke that tool.
  // 'rotate' is the separate signing-secret verb, as in connections/embed.
  toolproviders: 'author',
  knowledge: 'author',     // models/AppKnowledgeBase.model.js
  connections: 'author',   // models/AppConnection.model.js (categories/providers need scope 'all')
  embed: 'rotate',         // models/AppEmbedKey.model.js
  calls: 'configure',      // routes/voiceCallsRouter.js — 'dial' is the separate spend-money verb
  voice: 'configure',
  companies: 'govern',     // models/AppCompany.model.js
  workflows: 'author',     // models/AppWorkflow.model.js — 'run' is the separate execute verb
  applications: 'govern',   // models/AppApplication.model.js
  // models/AppMockEnvironment.model.js — 'configure' is the separate verb for proxy/
  // record mode and the stored upstream credentials, which spend real outbound calls.
  mockapi: 'author',
  // models/AppUiAudit.model.js — starting a run signs into a real application and spends
  // vision credits, so it is the write, and plain 'view' cannot reach it.
  uiaudits: 'author',
  // routes/browserDevicesRouter.js — 'pair' is the create/revoke verb (which browsers
  // exist at all). 'drive' is the separate, narrower verb for widening a device's origin
  // allowlist, which is the act that actually grants an agent reach into a live session.
  browser: 'pair',
};

/** `govern` is the safest fallback: it is the narrowest verb an unknown module is
 *  likely to have, so an unmapped module hides its button rather than showing a
 *  button the server will 403. */
export function writeActionFor(module) {
  return WRITE_ACTION[module] || 'govern';
}

export default WRITE_ACTION;
